// src/pages/ProjectsPage.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Typography, message, Space, Tooltip, Tag, DatePicker } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, ProjectOutlined } from '@ant-design/icons';
import { mockProjects } from '../data/mockProjects';
import { mockClients } from '../data/mockData';
import { Project } from '../types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>(mockProjects);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const getClientName = (clientId: string) => {
    const client = mockClients.find(c => c.id === clientId);
    return client?.companyName || 'N/A';
  };

  const getStatusTag = (status: Project['status']) => {
    const config = {
      'active': { color: 'success', text: 'Activo' },
      'completed': { color: 'default', text: 'Completado' },
      'paused': { color: 'warning', text: 'Pausado' },
    };
    const { color, text } = config[status];
    return <Tag color={color}>{text}</Tag>;
  };

  const columns: ColumnsType<Project> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <ProjectOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (clientId: string) => getClientName(clientId),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: Project['status']) => getStatusTag(status),
      filters: [
        { text: 'Activo', value: 'active' },
        { text: 'Completado', value: 'completed' },
        { text: 'Pausado', value: 'paused' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => new Date(date).toLocaleDateString('es-ES'),
    },
    {
      title: 'Fecha Fin',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date?: string) => date ? new Date(date).toLocaleDateString('es-ES') : 'Sin definir',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, record: Project) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewProject(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditProject(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteProject(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleAddProject = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = (values: any) => {
    const newProject: Project = {
      id: `p${projects.length + 1}`,
      name: values.name,
      clientId: values.clientId,
      description: values.description,
      status: values.status,
      startDate: values.dates[0].toISOString(),
      endDate: values.dates[1] ? values.dates[1].toISOString() : undefined,
    };
    
    setProjects([...projects, newProject]);
    message.success('Proyecto creado exitosamente');
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setIsViewModalOpen(true);
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    editForm.setFieldsValue({
      ...project,
      dates: [dayjs(project.startDate), project.endDate ? dayjs(project.endDate) : null],
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (values: any) => {
    if (!selectedProject) return;
    
    const updatedProjects = projects.map(p => 
      p.id === selectedProject.id ? {
        ...p,
        name: values.name,
        clientId: values.clientId,
        description: values.description,
        status: values.status,
        startDate: values.dates[0].toISOString(),
        endDate: values.dates[1] ? values.dates[1].toISOString() : undefined,
      } : p
    );
    
    setProjects(updatedProjects);
    message.success('Proyecto actualizado exitosamente');
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedProject(null);
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedProject(null);
  };

  const handleDeleteProject = (project: Project) => {
    modal.confirm({
      title: '¿Está seguro de eliminar este proyecto?',
      icon: <ExclamationCircleOutlined />,
      content: `Se eliminará el proyecto "${project.name}". Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        const updatedProjects = projects.filter(p => p.id !== project.id);
        setProjects(updatedProjects);
        message.success('Proyecto eliminado exitosamente');
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Gestión de Proyectos</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProject}>
          Nuevo Proyecto
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={projects}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Modal de Creación */}
      <Modal
        title="Crear Nuevo Proyecto"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Nombre del Proyecto"
            name="name"
            rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
          >
            <Input placeholder="Ej: Auditoría Laboral Q4 2025" />
          </Form.Item>

          <Form.Item
            label="Cliente"
            name="clientId"
            rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
          >
            <Select placeholder="Seleccione el cliente">
              {mockClients.map(client => (
                <Select.Option key={client.id} value={client.id}>
                  {client.companyName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Descripción"
            name="description"
          >
            <Input.TextArea rows={3} placeholder="Descripción del proyecto" />
          </Form.Item>

          <Form.Item
            label="Estado"
            name="status"
            rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
            initialValue="active"
          >
            <Select placeholder="Seleccione el estado">
              <Select.Option value="active">Activo</Select.Option>
              <Select.Option value="completed">Completado</Select.Option>
              <Select.Option value="paused">Pausado</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Fechas (Inicio - Fin)"
            name="dates"
            rules={[{ required: true, message: 'Por favor seleccione las fechas' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Crear Proyecto
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Edición */}
      <Modal
        title="Editar Proyecto"
        open={isEditModalOpen}
        onCancel={handleCancelEdit}
        footer={null}
        width={700}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="Nombre del Proyecto"
            name="name"
            rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
          >
            <Input placeholder="Ej: Auditoría Laboral Q4 2025" />
          </Form.Item>

          <Form.Item
            label="Cliente"
            name="clientId"
            rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
          >
            <Select placeholder="Seleccione el cliente">
              {mockClients.map(client => (
                <Select.Option key={client.id} value={client.id}>
                  {client.companyName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Descripción"
            name="description"
          >
            <Input.TextArea rows={3} placeholder="Descripción del proyecto" />
          </Form.Item>

          <Form.Item
            label="Estado"
            name="status"
            rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
          >
            <Select placeholder="Seleccione el estado">
              <Select.Option value="active">Activo</Select.Option>
              <Select.Option value="completed">Completado</Select.Option>
              <Select.Option value="paused">Pausado</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Fechas (Inicio - Fin)"
            name="dates"
            rules={[{ required: true, message: 'Por favor seleccione las fechas' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancelEdit}>Cancelar</Button>
              <Button type="primary" htmlType="submit">Guardar Cambios</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Detalles */}
      <Modal
        title="Detalles del Proyecto"
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Cerrar
          </Button>
        ]}
      >
        {selectedProject && (
          <div>
            <p><strong>Nombre:</strong> {selectedProject.name}</p>
            <p><strong>Cliente:</strong> {getClientName(selectedProject.clientId)}</p>
            <p><strong>Estado:</strong> {getStatusTag(selectedProject.status)}</p>
            <p><strong>Descripción:</strong> {selectedProject.description || 'Sin descripción'}</p>
            <p><strong>Fecha de Inicio:</strong> {new Date(selectedProject.startDate).toLocaleDateString('es-ES')}</p>
            <p><strong>Fecha de Fin:</strong> {selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString('es-ES') : 'Sin definir'}</p>
            <p><strong>ID:</strong> {selectedProject.id}</p>
          </div>
        )}
      </Modal>

      {contextHolder}
    </div>
  );
};

export default ProjectsPage;
